import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "system-design",
  name: "システム設計",
  nameEn: "System Design",
  seo: {
    title:
      "システム設計を学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "アーキテクチャ設計、マイクロサービス、DB設計、負荷分散、可用性設計を実践的に学べるゆめさくエンジニア。テックリード・アーキテクトへのキャリアを目指しましょう。",
    url: "/columns/system-design-programming-school",
  },
  hero: {
    title: "システム設計を学べる女性向けプログラミングスクール",
    englishTitle: "Learn System Design",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "システム設計" },
  ],
  overview: {
    heading: "システム設計とは？",
    paragraphs: [
      "システム設計とは、ソフトウェアシステムの全体構造（アーキテクチャ）を設計する技術・プロセスの総称です。機能要件・非機能要件を満たすために、コンポーネントの分割、データベースの設計、通信方式の選定、スケーラビリティ・可用性の確保などを行います。",
      "マイクロサービスアーキテクチャ、モノリスからの分割戦略、イベント駆動設計、CQRS、データベースシャーディングなど、大規模システムを安定的に運用するための設計パターンを理解することが重要です。",
      "システム設計はシニアエンジニアやテックリードに求められる最も重要なスキルの一つです。GAFAMをはじめとするテック企業の面接でも「システムデザイン面接」が必須項目となっており、エンジニアとしてのキャリアを上げるための核心的なスキルです。",
    ],
    useCases: [
      {
        label: "大規模Webサービスの設計",
        description:
          "数百万〜数億ユーザーが利用するWebサービスのアーキテクチャを設計し、スケーラビリティと可用性を確保します。",
      },
      {
        label: "マイクロサービスへの移行",
        description:
          "モノリシックなアプリケーションを適切な粒度のマイクロサービスに分割し、開発チームの独立性とデプロイ速度を向上させます。",
      },
      {
        label: "データベース設計・最適化",
        description:
          "正規化、インデックス設計、リードレプリカ、キャッシュ戦略など、データアクセスのパフォーマンスと整合性を両立する設計を行います。",
      },
      {
        label: "可用性・障害耐性の設計",
        description:
          "冗長化、ロードバランシング、サーキットブレーカー、グレースフルデグラデーションなど、障害に強いシステムを設計します。",
      },
    ],
  },
  whyLearn: {
    heading: "システム設計を学ぶべき3つの理由",
    items: [
      {
        title: "シニアエンジニアへの必須スキル",
        description:
          "コードを書くだけでなく、システム全体を俯瞰して設計できる能力は、テックリードやアーキテクトなどのシニアポジションに不可欠です。システム設計を学ぶことで、エンジニアとしてのキャリアの天井を大きく引き上げられます。",
      },
      {
        title: "テック企業の面接で必ず問われる",
        description:
          "GAFAMをはじめとするテック企業の技術面接では、システムデザイン問題が必須です。「URLShortenerを設計せよ」「Twitterのタイムラインを設計せよ」といった問題に答える力が求められます。",
      },
      {
        title: "年収1000万円超のポジションへの近道",
        description:
          "アーキテクト、テックリードなどのシステム設計を主導するポジションは、エンジニア職の中でも最高水準の年収が期待できます。設計力は経験とともに価値が上がり続けるスキルです。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでシステム設計を学ぶ",
    paragraphs: [
      "ゆめさくエンジニアでは、上級Phaseのカリキュラムとしてシステム設計を学びます。Web開発やデータベースの実践経験を積んだ上で、実際のサービスを題材にしたアーキテクチャ設計に取り組みます。",
      "教科書的な知識だけでなく、「なぜこの設計を選ぶのか」「トレードオフは何か」を考え抜く思考力を養い、システムデザイン面接にも対応できる実力を身につけます。",
    ],
    topics: [
      "スケーラビリティの基礎（水平・垂直スケーリング）",
      "ロードバランシングとキャッシュ戦略",
      "データベース設計（正規化・インデックス・シャーディング）",
      "マイクロサービスアーキテクチャの設計原則",
      "メッセージキュー・イベント駆動設計",
      "可用性・障害耐性の設計パターン",
      "実践プロジェクト：大規模Webサービスのアーキテクチャ設計",
    ],
  },
  careerPaths: {
    heading: "システム設計を活かせるキャリア",
    items: [
      {
        title: "テックリード",
        description:
          "開発チームの技術的な意思決定を主導し、システムアーキテクチャの設計・レビューを担当します。コードの品質と設計の一貫性を保ちながら、チーム全体の技術力向上をリードします。",
        salary: "700〜1,200万円",
      },
      {
        title: "アーキテクト",
        description:
          "大規模システムの全体設計を担当する専門職です。技術選定、アーキテクチャ決定、パフォーマンス設計など、システムの根幹に関わる意思決定を行います。",
        salary: "800〜1,400万円",
      },
      {
        title: "シニアエンジニア",
        description:
          "高度な設計力と実装力を兼ね備えたエンジニアです。機能設計からパフォーマンス最適化、コードレビューまで、開発プロセス全体の品質を底上げする役割を担います。",
        salary: "600〜1,000万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "12〜18ヶ月（実務経験なしでは理解しにくい）",
        luagate: "4〜6ヶ月（実際のサービス設計を題材に効率的に学習）",
      },
      {
        label: "挫折率",
        selfStudy: "抽象的な概念が多く、実感を伴わず挫折しやすい",
        luagate: "具体的なサービスの設計演習で実践的に理解",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "設計の良し悪しを自分で判断できない",
        luagate: "メンターとの設計レビューでフィードバックを得られる",
      },
      {
        label: "質問対応",
        selfStudy: "設計のトレードオフに関する議論相手がいない",
        luagate: "現役アーキテクトが設計判断の根拠を丁寧に解説",
      },
      {
        label: "就職サポート",
        selfStudy: "システムデザイン面接の対策が難しい",
        luagate: "模擬システムデザイン面接で実践力を磨く",
      },
    ],
  },
  faq: [
    {
      question: "システム設計を学ぶのにどのくらいの開発経験が必要ですか？",
      answer:
        "Web開発の基礎（フロントエンド・バックエンド・データベース）を一通り経験していることが望ましいです。ゆめさくエンジニアでは段階的なカリキュラムで基礎を固めた上でシステム設計に進むため、実務経験がなくても体系的に学べます。",
    },
    {
      question: "システム設計と普通のプログラミングの違いは何ですか？",
      answer:
        "プログラミングは「コードを書いて機能を実装する」スキルですが、システム設計は「複数のコンポーネントをどう組み合わせてシステム全体を構築するか」を考えるスキルです。木を見るのがプログラミング、森を見るのがシステム設計と言えます。",
    },
    {
      question: "システム設計の学習に数学やアルゴリズムの知識は必要ですか？",
      answer:
        "高度な数学は不要ですが、計算量（O記法）の基本的な理解や、データ構造の知識があると学習がスムーズです。分散システムにおけるCAP定理などの理論的な概念も学びますが、実践的なアプローチで理解できるよう工夫しています。",
    },
    {
      question: "システム設計のスキルは転職面接で本当に評価されますか？",
      answer:
        "はい、特にテック企業やスタートアップでは、システムデザイン面接が選考プロセスに組み込まれています。設計力を持つエンジニアは希少なため、年収交渉でも有利に働きます。",
    },
  ],
  relatedLanguages: [
    { slug: "rest-api", name: "REST API" },
    { slug: "aws", name: "AWS" },
    { slug: "java", name: "Java" },
    { slug: "upstream", name: "上流工程" },
  ],
};
